import { apolloClient } from '../apollo';
import { type AppInfo } from '../types';
import {
  FindManyHandlerAppsDocument,
  FindManyHandlerAppsQuery,
  FilteringConnector,
  FilteringType,
  HandlerAppFilteringField,
} from '../types/graphql';

export type AppLocaleInfo = {
  locale: string;
  name: string;
  nameWithArticle?: string;
};

export type HandlerAppInfo = {
  uuid: string;
  name: string;
  base64Logo: string;
  countries: string[];
  locales: AppLocaleInfo[];
};

/**
 * Resolve the app name including its definite article (e.g. "the NL Wallet")
 * for the given locale, suitable for use inside prose. Falls back to the
 * localized plain name, then to the app's base name.
 */
function resolveAppNameWithArticle(
  app: HandlerAppInfo | undefined,
  locale: string,
): string | undefined {
  if (!app) return undefined;
  const match = app.locales.find((l) => l.locale === locale);
  return match?.nameWithArticle ?? match?.name ?? app.name;
}

/** The app's display info for the selected token, for headings and logos. */
export function toAppInfo(
  app: HandlerAppInfo | undefined,
  locale: string,
): AppInfo | undefined {
  if (!app) return undefined;
  return {
    name: app.name,
    nameWithArticle: resolveAppNameWithArticle(app, locale),
    logo: app.base64Logo,
  };
}

export async function fetchHandlerApps(
  handlerAppUuids: string[],
): Promise<Record<string, HandlerAppInfo>> {
  const { data } = await apolloClient.query<FindManyHandlerAppsQuery>({
    query: FindManyHandlerAppsDocument,
    variables: {
      input: {
        filtering: handlerAppUuids.map((uuid) => ({
          connector: FilteringConnector.Disjunction,
          field: HandlerAppFilteringField.Uuid,
          value: uuid,
          type: FilteringType.Exact,
        })),
        pagination: { first: 50 },
      },
    },
  });

  const map: Record<string, HandlerAppInfo> = {};
  for (const edge of data.findManyHandlerApps.edges) {
    map[edge.node.uuid] = {
      uuid: edge.node.uuid,
      name: edge.node.app.name,
      base64Logo: edge.node.app.base64Logo,
      countries: edge.node.app.countries,
      locales: edge.node.app.locales.edges.map((localeEdge) => ({
        locale: localeEdge.node.locale,
        name: localeEdge.node.name,
        nameWithArticle: localeEdge.node.nameWithArticle,
      })),
    };
  }

  return map;
}
